<template>
  <HrPageLayout
    title="Appraisal Cycle Setup"
    subtitle="Create annual cycles, form rules, rating scales, periods and launch to employees"
    icon="📅"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Performance Management' }, { label: 'Cycle Setup' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" @click="$router.push({ name: 'pas-forms' })"><i class="bx bx-file"></i> Forms</button>
      <button type="button" class="hr-btn hr-btn-primary" @click.stop.prevent="openNew"><i class="bx bx-plus"></i> New cycle</button>
    </template>

    <div class="cs">
      <div class="cs-card">
        <div v-if="loading" class="muted pad">Loading…</div>
        <table v-else class="cs-table">
          <thead>
            <tr><th>Name</th><th>FY</th><th>Target period</th><th>Mid-year</th><th>End-year</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="c in cycles" :key="c.id">
              <td><strong>{{ c.cycle_name }}</strong></td>
              <td>{{ c.financial_year }}</td>
              <td>{{ c.target_start }} → {{ c.target_end }}</td>
              <td>{{ c.midyear_start }} → {{ c.midyear_end }}</td>
              <td>{{ c.endyear_start }} → {{ c.endyear_end }}</td>
              <td><span class="st" :class="c.status">{{ c.status }}</span></td>
              <td class="acts">
                <button type="button" class="hr-btn hr-btn-sm" @click="edit(c)">Edit</button>
                <button type="button" v-if="c.status === 'draft'" class="hr-btn hr-btn-sm hr-btn-success" @click="launch(c)">Launch</button>
                <button type="button" v-if="c.status === 'active'" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="closeCycle(c)">Close</button>
              </td>
            </tr>
            <tr v-if="!cycles.length"><td colspan="7" class="muted pad">No cycles yet.</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </HrPageLayout>

  <!-- Teleport-style root overlay: avoid Bootstrap .modal { display:none } and stacking under layout -->
  <div
    v-if="show"
    class="pas-cs-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="pas-cs-title"
    @click.self="show = false"
    @keydown.esc="show = false"
  >
    <div class="pas-cs-dialog" @click.stop>
      <div class="pas-cs-head">
        <h3 id="pas-cs-title">{{ form.id ? 'Edit cycle' : 'New appraisal cycle' }}</h3>
        <button type="button" class="pas-cs-x" aria-label="Close" @click="show = false">&times;</button>
      </div>
      <div class="pas-cs-body">
        <div class="pas-cs-grid">
          <label>Cycle name * <input v-model="form.cycle_name" placeholder="FY 2026/2027 Performance Appraisal"></label>
          <label>Financial year * <input v-model="form.financial_year" placeholder="2026/2027"></label>
          <label>Target start <input type="date" v-model="form.target_start"></label>
          <label>Target end <input type="date" v-model="form.target_end"></label>
          <label>Mid-year start <input type="date" v-model="form.midyear_start"></label>
          <label>Mid-year end <input type="date" v-model="form.midyear_end"></label>
          <label>End-year start <input type="date" v-model="form.endyear_start"></label>
          <label>End-year end <input type="date" v-model="form.endyear_end"></label>
          <label>Form H &amp; below label <input v-model="form.form_h_below_label"></label>
          <label>Form J &amp; above label <input v-model="form.form_j_above_label"></label>
          <label>Reminder days <input type="number" min="0" v-model.number="form.reminder_days"></label>
          <label class="pas-cs-chk"><input type="checkbox" v-model="form.scope_all"> Apply to all departments</label>
        </div>
        <div v-if="!form.scope_all" class="pas-cs-depts">
          <p>Select departments</p>
          <label v-for="d in departments" :key="d" class="pas-cs-dchk"><input type="checkbox" :value="d" v-model="form.departments"> {{ d }}</label>
        </div>
        <p class="pas-cs-hint">Default rating scale (1–5). Forms for each job group are managed under <strong>Appraisal Forms</strong>. After save, click <strong>Launch</strong> to create employee assignments.</p>
        <p v-if="permHint" class="pas-cs-err">{{ permHint }}</p>
      </div>
      <div class="pas-cs-actions">
        <button type="button" class="hr-btn hr-btn-secondary" @click="show = false">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save cycle' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

const empty = () => ({
  id: null,
  cycle_name: "",
  financial_year: "",
  target_start: "",
  target_end: "",
  midyear_start: "",
  midyear_end: "",
  endyear_start: "",
  endyear_end: "",
  form_h_below_label: "Job Group H and below",
  form_j_above_label: "Job Group J and above",
  reminder_days: 7,
  scope_all: true,
  departments: []
});

export default {
  name: "PasCycleSetup",
  components: { HrPageLayout },
  data() {
    return { loading: false, saving: false, cycles: [], departments: [], show: false, form: empty(), permHint: "" };
  },
  mounted() {
    this.loadDepartments();
    this.load();
  },
  methods: {
    async loadDepartments() {
      try {
        const fd = new FormData();
        fd.append("function", "getDepartments");
        const res = await execute(fd, authUrl);
        this.departments = (res?.data?.data?.departments || []).map((d) => d.departmentName).filter(Boolean).sort();
      } catch (_) { this.departments = []; }
    },
    async load() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasListCycles");
        const res = await execute(fd, authUrl);
        this.cycles = res?.data?.data?.cycles || [];
        if (res?.data && res.data.success === false) {
          this.permHint = res.data.message || "Permission denied listing cycles";
        }
      } finally { this.loading = false; }
    },
    openNew() {
      this.form = empty();
      this.permHint = "";
      this.show = true;
    },
    async edit(c) {
      const fd = new FormData();
      fd.append("function", "pasGetCycle");
      fd.append("id", String(c.id));
      const res = await execute(fd, authUrl);
      const cy = res?.data?.data?.cycle || c;
      this.form = {
        id: cy.id,
        cycle_name: cy.cycle_name || "",
        financial_year: cy.financial_year || "",
        target_start: (cy.target_start || "").slice(0, 10),
        target_end: (cy.target_end || "").slice(0, 10),
        midyear_start: (cy.midyear_start || "").slice(0, 10),
        midyear_end: (cy.midyear_end || "").slice(0, 10),
        endyear_start: (cy.endyear_start || "").slice(0, 10),
        endyear_end: (cy.endyear_end || "").slice(0, 10),
        form_h_below_label: cy.form_h_below_label || "Job Group H and below",
        form_j_above_label: cy.form_j_above_label || "Job Group J and above",
        reminder_days: Number(cy.reminder_days || 7),
        scope_all: Number(cy.scope_all) !== 0,
        departments: cy.departments || []
      };
      this.show = true;
    },
    async save() {
      if (!this.form.cycle_name || !this.form.financial_year) {
        toast.error("Name and financial year required");
        return;
      }
      this.saving = true;
      this.permHint = "";
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveCycle");
        // Never send id=0/null as text that confuses PHP
        if (this.form.id) fd.append("id", String(this.form.id));
        fd.append("cycle_name", String(this.form.cycle_name || "").trim());
        fd.append("financial_year", String(this.form.financial_year || "").trim());
        fd.append("target_start", this.form.target_start || "");
        fd.append("target_end", this.form.target_end || "");
        fd.append("midyear_start", this.form.midyear_start || "");
        fd.append("midyear_end", this.form.midyear_end || "");
        fd.append("endyear_start", this.form.endyear_start || "");
        fd.append("endyear_end", this.form.endyear_end || "");
        fd.append("form_h_below_label", this.form.form_h_below_label || "Job Group H and below");
        fd.append("form_j_above_label", this.form.form_j_above_label || "Job Group J and above");
        fd.append("reminder_days", String(this.form.reminder_days ?? 7));
        fd.append("scope_all", this.form.scope_all ? "1" : "0");
        fd.append("departments", JSON.stringify(this.form.departments || []));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Cycle saved");
          this.show = false;
          this.permHint = "";
          this.load();
        } else {
          const msg = res?.data?.message || "Save failed";
          this.permHint = msg;
          toast.error(msg);
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Save failed";
        this.permHint = msg;
        toast.error(msg);
      } finally {
        this.saving = false;
      }
    },
    async launch(c) {
      if (!window.confirm("Launch this cycle and create employee assignments? Other active cycles will be closed.")) return;
      const fd = new FormData();
      fd.append("function", "pasLaunchCycle");
      fd.append("id", String(c.id));
      fd.append("deactivate_others", "1");
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success(res.data.message || "Launched");
        this.load();
      } else toast.error(res?.data?.message || "Launch failed");
    },
    async closeCycle(c) {
      if (!window.confirm("Close this cycle?")) return;
      const fd = new FormData();
      fd.append("function", "pasCloseCycle");
      fd.append("id", String(c.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) { toast.success("Closed"); this.load(); }
      else toast.error(res?.data?.message || "Close failed");
    }
  }
};
</script>

<style scoped>
.cs-card { background:#fff; border:1px solid #e2e8f0; border-radius:14px; overflow:auto; }
.cs-table { width:100%; border-collapse:collapse; font-size:.88rem; }
.cs-table th, .cs-table td { padding:.7rem .85rem; border-bottom:1px solid #f1f5f9; text-align:left; }
.cs-table th { background:#f8fafc; font-size:.72rem; text-transform:uppercase; color:#64748b; }
.st { padding:.15rem .5rem; border-radius:999px; font-size:.72rem; font-weight:700; text-transform:capitalize; }
.st.draft { background:#f1f5f9; }
.st.active { background:#d1fae5; color:#065f46; }
.st.closed { background:#fee2e2; color:#991b1b; }
.acts { display:flex; gap:4px; flex-wrap:wrap; }
.muted { color:#94a3b8; }
.pad { padding:1.5rem; }

/* Custom names — do NOT use Bootstrap's .modal (display:none by default) */
.pas-cs-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.5);
  display: flex !important;
  align-items: center;
  justify-content: center;
  z-index: 10050 !important;
  padding: 1rem;
  box-sizing: border-box;
}
.pas-cs-dialog {
  background: #fff;
  width: min(820px, 96vw);
  max-height: 92vh;
  overflow: auto;
  border-radius: 14px;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.25);
  display: block !important;
}
.pas-cs-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.pas-cs-head h3 { margin: 0; font-size: 1.05rem; color: #0f172a; }
.pas-cs-x {
  border: none;
  background: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
  padding: 0 .25rem;
}
.pas-cs-body { padding: 1.1rem 1.2rem; }
.pas-cs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .75rem;
}
.pas-cs-grid label {
  display: flex;
  flex-direction: column;
  gap: .3rem;
  font-size: .75rem;
  font-weight: 700;
  color: #475569;
}
.pas-cs-grid input:not([type="checkbox"]) {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: .5rem .65rem;
  background: #f8fafc;
  font-weight: 500;
  color: #0f172a;
}
.pas-cs-chk {
  flex-direction: row !important;
  align-items: center !important;
  gap: .5rem !important;
  font-weight: 600 !important;
}
.pas-cs-depts {
  margin-top: .75rem;
  max-height: 160px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: .5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .35rem;
}
.pas-cs-dchk { font-size: .85rem; font-weight: 500; }
.pas-cs-hint { font-size: .8rem; color: #64748b; margin: .75rem 0 0; }
.pas-cs-err { font-size: .85rem; color: #b91c1c; font-weight: 600; margin: .5rem 0 0; }
.pas-cs-actions {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  padding: 0 1.2rem 1.1rem;
}
@media (max-width: 720px) {
  .pas-cs-grid,
  .pas-cs-depts { grid-template-columns: 1fr; }
}
</style>
